import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic } from 'antd';
import {
  ToolOutlined,
  ShoppingOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getAllExecutionResources,
  getAllServices,
  getQuotes,
} from '../services/supplierService';
import { ResourceType, EQUIPMENT_TYPES } from '../models/Resource';

const countOf = (data: any, key: string): number => {
  const list = data?.[key] ?? data;
  return Array.isArray(list) ? list.length : 0;
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [equipmentCount, setEquipmentCount] = useState(0);
  const [consumablesCount, setConsumablesCount] = useState(0);
  const [teamCount, setTeamCount] = useState(0);
  const [servicesCount, setServicesCount] = useState(0);
  const [requestsCount, setRequestsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      const [resources, services, quotes] = await Promise.allSettled([
        getAllExecutionResources(),
        getAllServices(),
        getQuotes(),
      ]);

      const resourceList =
        resources.status === 'fulfilled'
          ? resources.value?.execution_resources ?? resources.value
          : [];
      const list = Array.isArray(resourceList) ? resourceList : [];

      setEquipmentCount(
        list.filter((item: any) => EQUIPMENT_TYPES.includes(item.type)).length
      );
      setConsumablesCount(
        list.filter((item: any) => item.type === ResourceType.CONSUMABLE).length
      );
      setTeamCount(
        list.filter((item: any) => item.type === ResourceType.MAN_POWER).length
      );
      setServicesCount(
        services.status === 'fulfilled' ? countOf(services.value, 'products') : 0
      );
      setRequestsCount(
        quotes.status === 'fulfilled' ? countOf(quotes.value, 'quotes') : 0
      );
      setLoading(false);
    };
    fetchCounts();
  }, []);

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0, marginBottom: 16 }}>
        {t('dashboard')}
      </h1>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              loading={loading}
              title={t('totalEquipment')}
              value={equipmentCount}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              loading={loading}
              title={t('totalConsumables')}
              value={consumablesCount}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#BD7423' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              loading={loading}
              title={t('totalTeamMembers')}
              value={teamCount}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              loading={loading}
              title={t('totalServices')}
              value={servicesCount}
              prefix={<CustomerServiceOutlined />}
              valueStyle={{ color: '#BD7423' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              loading={loading}
              title={t('totalRequests')}
              value={requestsCount}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: 24 }}>
        <h2 style={{ color: '#334E3B' }}>{t('welcome')}</h2>
        <p>{t('welcomeMessage')}</p>
        <ul>
          <li>
            <strong>{t('equipment')}</strong> - {t('manageEquipment')}
          </li>
          <li>
            <strong>{t('consumables')}</strong> - {t('manageConsumables')}
          </li>
          <li>
            <strong>{t('teams')}</strong> - {t('manageTeams')}
          </li>
          <li>
            <strong>{t('services')}</strong> - {t('manageServices')}
          </li>
        </ul>
      </Card>
    </div>
  );
};

export default Dashboard;
